/// <amd-module name="Core/Medius.Core.Web/Scripts/components/emailStatusTab/common/emailStatusUtils"/>

import {
    IconStatusCheckCircleFill,
    IconStatusCheckCircleRegular,
    IconStatusQuestionFill,
    IconStatusXCircleFill,
    IconStatusXCircleRegular,
    IconWithTooltip
} from '@medius/ui-controls';
import { EMAIL_STATUSES, EmailStatus } from '../models/emailEntryDto';
import * as React from 'react';
import { translate } from 'Core/Medius.Core.Web/Scripts/lib/globalization';

/**
 * Common email status-related utilities and types
 */

/**
 * Creates tooltip text for email status with optional reason
 * @param status The email status
 * @param reason Optional reason for failed/deferred statuses
 * @returns Formatted tooltip text
 */
export function createStatusTooltip(status: string, reason?: string): string {
    return reason ? `${status}. ${reason}` : status;
}

/**
 * Get the translation key for a generic email status
 * @param status The generic email status
 * @returns Translation key for Core translations
 */
export function getEmailStatusTranslationKey(status: EmailStatus): string {
    switch (status) {
        case EMAIL_STATUSES.Green:
            return '#Core/emailDelivered';
        case EMAIL_STATUSES.HollowGreen:
            return '#Core/emailProcessing';
        case EMAIL_STATUSES.Red:
            return '#Core/emailFailed';
        case EMAIL_STATUSES.HollowRed:
            return '#Core/emailDeferred';
        default:
            return '#Core/emailUnknown';
    }
}

export function getOverallStatusIcon(emailStatus: EmailStatus): any {
    if (emailStatus === EMAIL_STATUSES.Green) {
        return <IconStatusCheckCircleFill color="ok" data-testid="delivered-icon" />;
    }

    if (emailStatus === EMAIL_STATUSES.HollowGreen) {
        return <IconStatusCheckCircleRegular color="ok" data-testid="delivered-hollow-icon" />;
    }

    if (emailStatus === EMAIL_STATUSES.Red) {
        return <IconStatusXCircleFill color="error" data-testid="failed-icon" />;
    }

    if (emailStatus === EMAIL_STATUSES.HollowRed) {
        return <IconStatusXCircleRegular color="error" data-testid="failed-hollow-icon" />;
    }

    if (emailStatus === EMAIL_STATUSES.Amber) {
        return <IconStatusQuestionFill color="warning" data-testid="unknown-icon" />;
    }
}

/**
 * Returns the recipient icon (with tooltip) for a given email status.
 * @param emailStatus Recipient's email status
 * @param latestEventReason Optional failure/deferred reason (used for Red status)
 */
export function getRecipientIcon(emailStatus: EmailStatus, latestEventReason?: string): React.ReactNode {
    const deliveryFailedMessage = `${translate('#Core/emailDeliveryFailed')}. ${latestEventReason ?? ''}`.trim();
    
    if (emailStatus === EMAIL_STATUSES.Green) {
        return <IconWithTooltip icon={<IconStatusCheckCircleFill color="ok" />} text={translate('#Core/emailDelivered')} data-testid="recipient-delivered-icon" />;
    }

    if (emailStatus === EMAIL_STATUSES.HollowGreen) {
        return <IconWithTooltip icon={<IconStatusCheckCircleRegular color="ok" />} text={translate('#Core/emailProcessing')} data-testid="recipient-delivered-hollow-icon" />;
    }

    if (emailStatus === EMAIL_STATUSES.Red) {
        return <IconWithTooltip icon={<IconStatusXCircleFill color="error" />} text={deliveryFailedMessage} data-testid="recipient-failed-icon" />;
    }

    if (emailStatus === EMAIL_STATUSES.HollowRed) {
        return <IconWithTooltip icon={<IconStatusXCircleRegular color="error" />} text={translate('#Core/emailDeliveryDeferred')} data-testid="recipient-failed-hollow-icon" />;
    }

    if (emailStatus === EMAIL_STATUSES.Amber) {
        return <IconWithTooltip icon={<IconStatusQuestionFill color="warning" />} text={translate('#Core/emailDeliveryStatusUnknown')} data-testid="recipient-unknown-icon" />;
    }

    return undefined;
}
